import React, { useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const Sidebar = ({ isOpen, isMobile, toggleSidebar }) => {
  const { role } = useAuth();
  const sidebarRef = useRef(null);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobile && isOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        toggleSidebar();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, isOpen, toggleSidebar]);

  // Define navigation items based on role with icons
  const getNavItems = () => {
    if (role === 'admin') {
      return [
        {
          path: '/admin/dashboard',
          label: 'Dashboard',
          icon: '📊',
          description: 'Overview and statistics'
        },
        {
          path: '/admin/volunteers',
          label: 'Volunteers',
          icon: '👨‍🌾',
          description: 'Manage field workers'
        },
        {
          path: '/admin/farmer-database',
          label: 'Farmer Database',
          icon: '🌾',
          description: 'API-connected farmer database'
        },
        {
          path: '/admin/companies',
          label: 'Companies',
          icon: '🏢',
          description: 'Partner organizations'
        },
        {
          path: '/admin/mapping',
          label: 'Mapping',
          icon: '🗺️',
          description: 'Create farmer-company connections'
        },
        {
          path: '/admin/mapped-data',
          label: 'Mapped Data',
          icon: '📋',
          description: 'View existing connections'
        },
      ];
    } else if (role === 'company') {
      return [
        {
          path: '/company/profile',
          label: 'Company Profile',
          icon: '🏢',
          description: 'View and edit company details'
        },
        {
          path: '/company/mapped-farmers',
          label: 'Mapped Farmers',
          icon: '🌾',
          description: 'View connected farmers'
        },
      ];
    }
    return [];
  };

  const navItems = getNavItems();

  // Group navigation items by category
  const mainNavItems = navItems.slice(0, 1); // Dashboard
  const dataNavItems = navItems.slice(1, 4); // Volunteers, Farmer Database, Companies
  const mappingNavItems = navItems.slice(4); // Mapping, Mapped Data

  return (
    <div
      ref={sidebarRef}
      style={{
        ...styles.sidebar,
        width: isMobile ? (isOpen ? '85%' : '0') : (isOpen ? 'var(--sidebar-width)' : 'var(--sidebar-collapsed-width)'),
        transform: isMobile ? (isOpen ? 'translateX(0)' : 'translateX(-100%)') : 'none',
        height: '100vh',
        top: 'var(--header-height)',
        zIndex: isMobile ? 100 : 50,
        boxShadow: isMobile && isOpen ? '0 0 15px rgba(0, 0, 0, 0.3)' : styles.sidebar.boxShadow,
        overflowY: isMobile ? 'auto' : 'hidden',
      }}
    >
      <div style={styles.sidebarHeader}>
        {isMobile && (
          <button
            onClick={toggleSidebar}
            style={styles.closeButton}
            aria-label="Close sidebar"
          >
            ✕
          </button>
        )}
        <div style={styles.headerText}>
          {role === 'admin' ? 'Admin Panel' : 'Company Panel'}
        </div>
      </div>

      <nav style={styles.nav}>
        <div style={styles.navSection}>
          <div style={styles.navSectionTitle}>Main</div>
          <ul style={styles.navList}>
            {mainNavItems.map((item, index) => (
              <li key={index} style={styles.navItem}>
                <NavLink
                  to={item.path}
                  style={({ isActive }) => ({
                    ...styles.navLink,
                    ...(isActive ? styles.activeNavLink : {}),
                  })}
                  title={item.description}
                  onClick={isMobile ? toggleSidebar : undefined}
                >
                  <span style={styles.navIcon}>{item.icon}</span>
                  <span style={{
                    ...styles.navLabel,
                    display: !isMobile && !isOpen ? 'none' : 'block',
                  }}>
                    {item.label}
                  </span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>

        {role === 'admin' && (
          <>
            <div style={styles.navSection}>
              <div style={styles.navSectionTitle}>Data Management</div>
              <ul style={styles.navList}>
                {dataNavItems.map((item, index) => (
                  <li key={index} style={styles.navItem}>
                    <NavLink
                      to={item.path}
                      style={({ isActive }) => ({
                        ...styles.navLink,
                        ...(isActive ? styles.activeNavLink : {}),
                      })}
                      title={item.description}
                      onClick={isMobile ? toggleSidebar : undefined}
                    >
                      <span style={styles.navIcon}>{item.icon}</span>
                      <span style={{
                        ...styles.navLabel,
                        display: !isMobile && !isOpen ? 'none' : 'block',
                      }}>
                        {item.label}
                      </span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>

            <div style={styles.navSection}>
              <div style={styles.navSectionTitle}>Mapping</div>
              <ul style={styles.navList}>
                {mappingNavItems.map((item, index) => (
                  <li key={index} style={styles.navItem}>
                    <NavLink
                      to={item.path}
                      style={({ isActive }) => ({
                        ...styles.navLink,
                        ...(isActive ? styles.activeNavLink : {}),
                      })}
                      title={item.description}
                      onClick={isMobile ? toggleSidebar : undefined}
                    >
                      <span style={styles.navIcon}>{item.icon}</span>
                      <span style={{
                        ...styles.navLabel,
                        display: !isMobile && !isOpen ? 'none' : 'block',
                      }}>
                        {item.label}
                      </span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        {role === 'company' && (
          <div style={styles.navSection}>
            <div style={styles.navSectionTitle}>Company</div>
            <ul style={styles.navList}>
              {navItems.map((item, index) => (
                <li key={index} style={styles.navItem}>
                  <NavLink
                    to={item.path}
                    style={({ isActive }) => ({
                      ...styles.navLink,
                      ...(isActive ? styles.activeNavLink : {}),
                    })}
                    title={item.description}
                    onClick={isMobile ? toggleSidebar : undefined}
                  >
                    <span style={styles.navIcon}>{item.icon}</span>
                    <span style={{
                      ...styles.navLabel,
                      display: !isMobile && !isOpen ? 'none' : 'block',
                    }}>
                      {item.label}
                    </span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>

      <div style={styles.sidebarFooter}>
        <div style={styles.footerText}>FarmConnect v1.0</div>
      </div>
    </div>
  );
};

const styles = {
  sidebar: {
    width: 'var(--sidebar-width)',
    backgroundColor: '#1e3a29', // Dark forest green
    color: 'white',
    height: 'calc(100vh - var(--header-height))', // Full height minus header
    position: 'fixed',
    left: 0,
    top: 'var(--header-height)', // Height of header
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '2px 0 10px rgba(0, 0, 0, 0.15)',
    zIndex: 50,
    backgroundImage: 'linear-gradient(to bottom, #1e3a29, #2a4a36)',
    overflow: 'hidden', // No scrollbar
    transition: 'all 0.3s ease',
  },
  closeButton: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: 'rgba(0, 0, 0, 0.2)',
    border: 'none',
    color: 'white',
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '16px',
    zIndex: 10,
  },
  sidebarHeader: {
    padding: '1.5rem 1rem',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'rgba(0, 0, 0, 0.1)',
  },
  headerIcon: {
    fontSize: '2rem',
    marginBottom: '0.5rem',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerText: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    letterSpacing: '0.5px',
    marginTop: '0.5rem',
  },
  nav: {
    flex: 1,
    padding: '0.5rem 0',
  },
  navSection: {
    marginBottom: '1.5rem',
  },
  navSectionTitle: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    color: 'rgba(255, 255, 255, 0.5)',
    padding: '0 1.5rem',
    marginBottom: '0.5rem',
  },
  navList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  navItem: {
    margin: '4px 0.5rem',
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.8rem 1rem',
    color: 'rgba(255, 255, 255, 0.8)',
    textDecoration: 'none',
    transition: 'all 0.2s',
    borderRadius: '6px',
    position: 'relative',
    overflow: 'hidden',
    margin: '2px 0',
  },
  navIcon: {
    marginRight: '0.75rem',
    fontSize: '1.1rem',
    width: '24px',
    textAlign: 'center',
    opacity: 0.9,
  },
  navLabel: {
    fontWeight: '500',
    fontSize: '0.95rem',
  },
  activeNavLink: {
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  },
  sidebarFooter: {
    padding: '0.75rem',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    textAlign: 'center',
    fontSize: '0.75rem',
    opacity: 0.6,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
  footerText: {
    margin: 0,
  },
};

export default Sidebar;
